import { Action, ListenerEffectAPI, type PayloadAction, type ThunkDispatch, UnknownAction, UnsubscribeListener, createListenerMiddleware } from '@reduxjs/toolkit';

import { type ActionSetStationId, type ActionSchedule, type OrchestratorState, setStationId, schedule, requestItem, type ActionRequestItem, type ChannelSelector, type ActionPlayItem, playItem, start, type ActionStart, type ActionUnloadItem, unloadItem, setChannelData, type ActionSetChannelData, stop, type ActionStopItem, stopItem, setVolume, type ActionSetVolume, type ActionStop, type ChannelState, mute, unmute, type ActionMute, type ActionUnmute, skip, ActionSkip, setJingleId, ActionSetJingleId, downloadItem, ActionDownloadItem } from '../slices/local/orchestrator';
import { type UserState, setChannelData as userSetChannelData, setStationId as userSetStationId } from '../slices/local/user';
import { All, type Player, assertPlayerComponent } from '../players';

export type BaseDispatch = ThunkDispatch<any, any, UnknownAction>;
export type ActionHandler = (dispatch: BaseDispatch) => Promise<void>;

type ListenerData = ChannelSelector & {
  state: OrchestratorState
  channelState: ChannelState
  userState: UserState
  originalState: OrchestratorState
  originalChannelState: ChannelState
  player: Player
  dispatch: BaseDispatch
  listenerApi: ListenerEffectAPI<any, BaseDispatch>
  update: (player: Player) => void
}

const listenerMiddleware = createListenerMiddleware();

export const getPlayer = (channelState: ChannelState, playerId: number, channel: number): Player => {
  return assertPlayerComponent(All, playerId, channel)
    .withState(channelState);
};

/** @internal */
type RTKActionCreator<Type extends string> = {
  (...args: any[]): Action<Type>;
  type: Type;
  match: (v: any) => v is any;
};

const listen = <C extends RTKActionCreator<any>> (
  actionCreator: C,
  callback: (action: ReturnType<C>, data: ListenerData) => Promise<void>
): UnsubscribeListener => (
    listenerMiddleware.startListening({
      actionCreator,
      effect: async (action: ReturnType<C>, listenerApi) => {
        const { payload: { playerId, channel } } = action as PayloadAction<ChannelSelector>;
        const { orchestrator: state, user: userState }: { orchestrator: OrchestratorState, user: UserState } = listenerApi.getState() as any;
        const channelState = state.players[playerId].channels[channel];
        const { orchestrator: originalState }: { orchestrator: OrchestratorState } = listenerApi.getOriginalState() as any;
        const originalChannelState = originalState.players[playerId]?.channels[channel];
        const { dispatch } = listenerApi;
        const player = getPlayer(channelState, playerId, channel);

        await callback(action, {
          playerId,
          channel,
          state,
          userState,
          originalState,
          channelState,
          originalChannelState,
          player,
          dispatch,
          listenerApi,
          update: player => {
            const { orchestrator: state }: { orchestrator: OrchestratorState } =
                        listenerApi.getState() as any;
            player.withState(state.players[playerId].channels[channel]);
          }
        });
      }
    })
  );

listen(setStationId, async (action: ActionSetStationId, {
  channelState,
  originalChannelState,
  playerId,
  channel,
  player,
  dispatch
}) => {
  if (!channelState.started) {
    dispatch(start({ playerId, channel }));
  }

  // Change the station
  const actionHandler = player.changeStation(originalChannelState.stationId!);
  if (actionHandler) {
    // changeStation might want to execute something directly instead from
    // the action queue
    await actionHandler(dispatch);
  }
  // Forward change to user reducer
  dispatch(userSetStationId(action.payload));
});

listen(setJingleId, async (action: ActionSetJingleId, {
  player,
  channelState,
  originalChannelState,
  userState,
  dispatch
}) => {
  if(channelState.jingleId !== undefined) {
    // Check if user can trigger this jingle
    const jingle = userState.jingles.find(e => e.jingleId === channelState.jingleId);
    if(jingle !== undefined) {
      // And trigger it
      const actionHandler = player.playJingle(jingle, originalChannelState.jingleId!);
      if(actionHandler) {
        // playJingle might want to execute something directly instead from
        // the action queue
        await actionHandler(dispatch);
      }
    }
  } else if(channelState.jingleForce) {
    const actionHandler = player.stopJingle(originalChannelState.jingleId!);
    if(actionHandler) {
      // stopJingle might want to execute something directly instead from
      // the action queue
      await actionHandler(dispatch);
    }
  }
});

listen(start, async (action: ActionStart, {
  state,
  channelState,
  originalChannelState,
  playerId,
  channel,
  player,
  dispatch,
  listenerApi,
  update
}) => {
  // Stop other scheduler(s) which are not allowed to run in the background
  Object.values(state.players).forEach(({ playerId: stopPlayerId, channels }) => {
    Object.values(channels).forEach(({ channel: stopChannel, started, runInBackground }) => {
      if (
        !(playerId === stopPlayerId && channel === stopChannel) &&
                started &&
                !runInBackground
      ) {
        dispatch(stop({
          playerId: stopPlayerId,
          channel: stopChannel
        }));
      }
    });
  });

  // Start scheduler
  if ((originalChannelState?.lifetime ?? 0) === 0) {
    /*const sentrySpan = getSentry().startInactiveSpan({
      name: 'player-lifecycle',
      forceTransaction: true,
      attributes: {
        playerId,
        channel
      }
    });
    player.setSentrySpan(sentrySpan);*/

    // Start scheduler for the selected channel
    dispatch(schedule({ playerId, channel }));
  }

  // Start monitor (@todo)
  if (player.isMonitorEnabled()) {
    const monitorTask = listenerApi.fork(async (forkApi) => {
      try {
        while (true) {
          update(player);
          player.monitor();
          await forkApi.delay(player.getMonitorInterval());
        }
      } catch (err) {
        console.error('Monitor error', err);
      }
    });

    await monitorTask.result;
  }
});

listen(setVolume, async (action: ActionSetVolume, {
  originalChannelState,
  player,
  dispatch
}) => {
  // player.changeVolume(originalChannelState.volume, !!action.payload.defer);
  const actionHandler = player.changeVolume(originalChannelState.volume, !!action.payload.defer);
  if (actionHandler) {
    // changeVolume might want to execute something directly instead from
    // the action queue
    await actionHandler(dispatch);
  }
});

listen(mute, async (action: ActionMute, { player }) => {
  player.mute();
});

listen(unmute, async (action: ActionUnmute, { player }) => {
  player.unmute();
});

listen(schedule, async (action: ActionSchedule, {
  channelState,
  userState,
  playerId,
  channel,
  player,
  dispatch
}) => {
  if (!channelState.started) {
    return;
  }

  player.withUserState(userState).schedule();
  for (let actionHandler; (actionHandler = player.pop()) !== undefined;) {
    await actionHandler(dispatch);
  }

  setTimeout(() => {
    dispatch(schedule({ playerId, channel }));
  }, player.getSchedulerInterval());
});

listen(stop, async (action: ActionStop, {
  player,
  dispatch
}) => {
  // Process remaining actions in scheduler queue
  for (let actionHandler; (actionHandler = player.pop()) !== undefined;) {
    await actionHandler(dispatch);
  }
});

listen(skip, async (action: ActionSkip, { player, dispatch }) => {
  player.skip();
});

listen(requestItem, async (action: ActionRequestItem, { player }) => {
  player.request(action.payload.index);
});

listen(playItem, async (action: ActionPlayItem, { player }) => {
  player.play(action.payload.index, action.payload.fromPosition);
});

listen(stopItem, async (action: ActionStopItem, { player }) => {
  player.stop(action.payload.index);
});

listen(downloadItem, async (action: ActionDownloadItem, { player }) => {
  player.downloaded(action.payload.index);
});

listen(unloadItem, async (action: ActionUnloadItem, { player }) => {
  player.unload(action.payload.index);
});

// Passthrough live channel data to user state
listenerMiddleware.startListening({
  actionCreator: setChannelData,
  effect: async (action: ActionSetChannelData, listenerApi) => {
    listenerApi.dispatch(userSetChannelData(action.payload));
  }
});

export {
  listenerMiddleware
};
